import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Tablet,
  CheckCircleOutline,
  PauseCircleOutline,
  SignalWifiOff,
  SendOutlined,
  TaskAlt,
} from '@mui/icons-material';
import { statsService } from '../services/api';
import { tokens } from '../theme';

// ─── Metric card individual ────────────────────────────────────────────────────
// Diseño intencional:
// - Sin hover-lift — los cards son contenedores informativos, no acciones
// - Jerarquía clara: número > label
// - Icono como acento cromático, no como protagonista
const MetricCard = ({ label, value, Icon, color, bg, border }) => (
  <Box
    sx={{
      backgroundColor: tokens.color.surface.paper,
      border: `1px solid ${tokens.color.border.default}`,
      borderRadius: tokens.radius.lg,
      p: 2.5,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      height: '100%',
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: tokens.radius.md,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color,
        '& .MuiSvgIcon-root': { fontSize: 20 },
      }}
    >
      <Icon />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '1.875rem',
          fontWeight: 700,
          lineHeight: 1.1,
          color: tokens.color.ink.primary,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: tokens.color.ink.muted,
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          mt: 0.5,
          display: 'block',
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const METRICS_SKELETON = [
  { label: 'Total',     key: ['devices', 'total'],     Icon: Tablet,               color: tokens.color.brand[500],         bg: tokens.color.brand[50],                  border: tokens.color.brand[100] },
  { label: 'Activos',   key: ['devices', 'active'],    Icon: CheckCircleOutline,   color: tokens.color.status.active.text,  bg: tokens.color.status.active.bg,           border: tokens.color.status.active.border },
  { label: 'Suspendidos',key:['devices','suspended'],  Icon: PauseCircleOutline,   color: tokens.color.status.suspended.text,bg: tokens.color.status.suspended.bg,       border: tokens.color.status.suspended.border },
  { label: 'Offline',   key: ['devices', 'offline'],   Icon: SignalWifiOff,        color: tokens.color.status.offline.text, bg: tokens.color.status.offline.bg,          border: tokens.color.status.offline.border },
  { label: 'Pendientes',key: ['commands', 'pending'],  Icon: SendOutlined,         color: tokens.color.status.pending.text, bg: tokens.color.status.pending.bg,          border: tokens.color.status.pending.border },
  { label: 'Ejecutados',key: ['commands', 'executed'], Icon: TaskAlt,              color: tokens.color.ink.secondary,       bg: tokens.color.surface.subtle,             border: tokens.color.border.default },
];

const get = (obj, [a, b]) => obj?.[a]?.[b] ?? 0;

const StatsCards = () => {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    statsService.getStats()
      .then((res) => {
        if (res.success) setStats(res.stats);
        else setError('No se pudieron cargar las estadísticas');
      })
      .catch(() => setError('Error de conexión'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} thickness={3} sx={{ color: tokens.color.brand[500] }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {METRICS_SKELETON.map((m) => (
        <Grid item xs={6} sm={4} md={2} key={m.label}>
          <MetricCard
            label={m.label}
            value={get(stats, m.key)}
            Icon={m.Icon}
            color={m.color}
            bg={m.bg}
            border={m.border}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;
